import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import {
  IImportantDocument,
  ISupportTicket,
  ITeamContact,
} from "@/interfaces/support.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const supportApi = createApi({
  reducerPath: "supportApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["support", "teamContact", "importantDocument"],
  endpoints: (builder) => ({
    //get All the support tickets
    getSupportTickets: builder.query<
      GeneralResponse<ISupportTicket[] | ISupportTicket>,
      { filters?: string; id?: string }
    >({
      query: ({ filters, id }) => {
        const basePath = `/workspace/${getWorkspace()}/support`;
        if (id) {
          return `${basePath}/${id}`;
        }

        return filters ? `${basePath}${filters}` : basePath;
      },
      providesTags: ["support"],
    }),
    //Mutate tickets
    mutateSupportTicket: builder.mutation<
      object,
      {
        data?: object;
        ticketId?: string;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, ticketId, method }) => ({
        url: `workspace/${getWorkspace()}/support/${ticketId ? `${ticketId}` : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["support"],
    }),
    //Add Comment to the ticket
    addCommentToTicket: builder.mutation<
      object,
      {
        data?: object;
        ticketId?: string;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, ticketId, method }) => ({
        url: `workspace/${getWorkspace()}/support/${ticketId ? `${ticketId}` : ""}/comment`,
        method,
        body: data,
      }),
      invalidatesTags: ["support"],
    }),

    //Team Contact
    getTeamContact: builder.query<
      GeneralResponse<ITeamContact[] | ITeamContact>,
      { filters?: string; id?: string }
    >({
      query: ({ filters, id }) => {
        const basePath = `/workspace/${getWorkspace()}/support/teams`;
        if (id) {
          return `${basePath}/${id}`;
        }

        return filters ? `${basePath}${filters}` : basePath;
      },
      providesTags: ["teamContact"],
    }),
    //Mutate Team Contact
    mutateTeamContact: builder.mutation<
      object,
      {
        data?: object;
        teamContactId?: string;
        method: "POST" | "PATCH" | "DELETE" | "PUT";
      }
    >({
      query: ({ data, teamContactId, method }) => ({
        url: `workspace/${getWorkspace()}/support/teams/${teamContactId ? `${teamContactId}` : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["teamContact"],
    }),

    //Important Document
    getImportantDocument: builder.query<
      GeneralResponse<IImportantDocument[] | IImportantDocument>,
      { filters?: string; id?: string }
    >({
      query: ({ filters, id }) => {
        const basePath = `/workspace/${getWorkspace()}/support/documents`;
        if (id) {
          return `${basePath}/${id}`;
        }

        return filters ? `${basePath}${filters}` : basePath;
      },
      providesTags: ["importantDocument"],
    }),
    //Mutate Important Document
    mutateImportantDocument: builder.mutation<
      object,
      {
        data?: object;
        importantDocumentId?: string;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, importantDocumentId, method }) => ({
        url: `workspace/${getWorkspace()}/support/documents/${importantDocumentId ? `${importantDocumentId}` : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["importantDocument"],
    }),
  }),
});

export const {
  useGetSupportTicketsQuery,
  useMutateSupportTicketMutation,
  useAddCommentToTicketMutation,
  useGetTeamContactQuery,
  useMutateTeamContactMutation,
  useGetImportantDocumentQuery,
  useMutateImportantDocumentMutation,
} = supportApi;
